// Copy and facts for the /welcome login page. Every number below is read from data the rest
// of the site already publishes (stats, hubs, the seafood catalogue), never typed in here:
// this project deliberately avoids trust claims it can't back (see data/appRatings.ts and
// the honesty note in data/operations.ts).

import { STATS } from "./stats";
import { HUBS } from "./operations";
import { seafoodItems } from "./seafood";
import { getWhatsAppLink } from "./whatsappContext";
import { ApiError } from "../utils/api";
import { firebaseErrorCode } from "../utils/phoneAuth";

const stat = (label: string) => STATS.find((s) => s.label === label)?.value ?? "";

export const FACTS = {
  partnerFishermen: stat("Partner Fishermen"),
  avgDelivery: stat("Avg. Delivery Time"),
  middlemen: stat("Middlemen"),
  traceability: stat("Catch Traceability"),
  liveHubs: HUBS.filter((hub) => hub.status === "live").length,
};

/** Today's-catch card on the hero: a real product with a real, credited photograph. */
export const HERO_ITEM =
  seafoodItems.find((item) => item.id === "karimeen-pearlspot") ?? seafoodItems[0];

export type StepId = "phone" | "otp" | "help";

export const STEP_TABS: { id: StepId; label: string }[] = [
  { id: "phone", label: "Mobile Login" },
  { id: "otp", label: "OTP Verification" },
  { id: "help", label: "Help & Concierge" },
];

export const SUPPORT_LINK = getWhatsAppLink("Hi, I need help logging in to ZenFresh.");

export const COPY = {
  brandTagline: "Fisherman-direct • Fresh in hours",
  heroBadge: "Same-day fresh",
  heroTitleStart: "Freshness landed",
  heroTitleEmphasis: "at dawn,",
  heroTitleEnd: "at your door.",
  heroBody:
    "Karimeen, vanjaram and tiger prawns bought straight from partner fishermen along India's coasts — no middlemen, no long cold-storage waits.",
  authLabel: "Authentication gateway",
  welcomeTitle: "Welcome to ZenFresh",
  welcomeBody: "Log in to track your orders and save your favourite catches.",
  guestTitle: "Explore Store as Guest",
  guestBody: "Browse today's catch, check prices and add to your basket anytime.",
  otpHint: "We'll text you a 6-digit code • No passwords needed",
};

const ERRORS: Record<string, string> = {
  VALIDATION_FAILED: "That didn't look right. Check the details and try again.",
  CAPTCHA_FAILED: "We couldn't verify you're human. Please try again.",
  RECAPTCHA_UNAVAILABLE: "The security check is unavailable right now. Please try again shortly.",
  RECAPTCHA_LOAD_FAILED: "We couldn't run the security check. Check your connection and try again.",
  RECAPTCHA_NOT_CONFIGURED: "WhatsApp login isn't available yet. You can still explore the store as a guest.",
  WHATSAPP_NOT_CONFIGURED: "WhatsApp login isn't available yet. You can still explore the store as a guest.",
  WHATSAPP_UNAVAILABLE: "We couldn't send the WhatsApp message. Please try again in a moment.",
  OTP_RESEND_TOO_SOON: "Please wait a moment before asking for another code.",
  OTP_RATE_LIMITED: "You've asked for too many codes. Please try again a little later.",
  OTP_INVALID: "That code isn't right. Check it and try again.",
  OTP_EXPIRED: "That code has expired. Request a new one.",
  OTP_LOCKED: "Too many wrong attempts. Request a new code.",
  ACCOUNT_DISABLED: "This account has been disabled. Please contact support on WhatsApp.",
  NOT_A_CUSTOMER_ACCOUNT: "This number can't be used to log in here. Please contact support on WhatsApp.",
  AUTH_UNAVAILABLE: "Sign-in is temporarily unavailable. Please try again shortly.",
  DATASTORE_UNAVAILABLE: "Sign-in is temporarily unavailable. Please try again shortly.",
  NETWORK_ERROR: "We can't reach ZenFresh right now. Check your connection and try again.",
  BAD_RESPONSE: "WhatsApp login isn't available right now. You can still explore the store as a guest.",
};

// Firebase Phone Auth error codes -> visitor-friendly copy.
const PHONE_AUTH_ERRORS: Record<string, string> = {
  "auth/invalid-phone-number": "Enter a valid 10-digit Indian mobile number.",
  "auth/invalid-verification-code": "That code isn't right. Check it and try again.",
  "auth/missing-verification-code": "Enter the 6-digit code from the SMS.",
  "auth/code-expired": "That code has expired. Request a new one.",
  "auth/session-expired": "That code has expired. Request a new one.",
  "auth/too-many-requests": "Too many attempts. Please wait a while and try again.",
  "auth/quota-exceeded": "We can't send more codes right now. Please try again later.",
  "auth/captcha-check-failed": "We couldn't verify you're human. Please try again.",
  "auth/network-request-failed": "We can't reach the sign-in service. Check your connection and try again.",
  "auth/operation-not-allowed": "SMS sign-in isn't available right now. You can still explore the store as a guest.",
  "auth/user-disabled": "This account has been disabled. Please contact support on WhatsApp.",
  "auth/web-storage-unsupported": "Your browser is blocking storage, which sign-in needs. Try another browser.",
};

const FALLBACK_ERROR = "Something went wrong. Please try again.";

/** A visitor-friendly message for whatever went wrong. Keyed on the stable error code. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return ERRORS[error.code] ?? FALLBACK_ERROR;
  const code = firebaseErrorCode(error);
  return (code && PHONE_AUTH_ERRORS[code]) || FALLBACK_ERROR;
}

export const HELP_TIPS: { title: string; body: string }[] = [
  {
    title: "The code hasn't arrived",
    body: "Check the number you entered and that you have mobile signal. SMS can take a minute; you can ask for a new code once the timer ends.",
  },
  {
    title: "It says the code is wrong or expired",
    body: "Codes last 5 minutes and work once. After 5 wrong tries we ask you to request a fresh one.",
  },
  {
    title: "I'd rather not log in",
    body: "You don't have to. Use “Explore Store as Guest” — logging in is only needed to track orders and save favourites.",
  },
];
